/**
 * デバイスのOSを判別するユーティリティ
 */

export type DeviceType = 'android' | 'ios' | 'other';

/**
 * ディープリンクの種類
 */
export type DeepLinkType = 'product' | 'profile' | 'none';

/**
 * ディープリンク情報
 */
export interface DeepLinkInfo {
  type: DeepLinkType;
  id: string | null;
}

/**
 * アプリのURL定数
 */
export const APP_URLS = {
  ANDROID_STORE: 'https://play.google.com/store/apps/details?id=com.app.art_flea',
  IOS_STORE: 'https://apps.apple.com/us/app/%E3%82%A2%E3%83%AA%E3%82%AF%E3%82%A4-%E8%8A%B8%E8%A1%93%E5%93%81%E3%83%95%E3%83%AA%E3%83%9E%E3%82%A2%E3%83%97%E3%83%AA/id6740814734',
  OFFICIAL_SITE: 'https://arikui.me/2025/05/18/%e3%82%a2%e3%83%aa%e3%82%af%e3%82%a4%e3%81%ae%e5%85%ac%e5%bc%8f%e3%82%b5%e3%82%a4%e3%83%88%e3%81%a7%e3%81%99/',
  CUSTOM_SCHEME: 'artflea://',
};

/**
 * User-Agentを解析してデバイスタイプを判別
 */
export const detectDevice = (): DeviceType => {
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
  
  // Androidデバイスの判定
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  
  // iOSデバイスの判定
  if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
    return 'ios';
  }
  
  // その他のデバイス
  return 'other';
};

/**
 * デバイスタイプに応じたリダイレクトURLを取得
 */
export const getRedirectUrl = (deviceType: DeviceType): string => {
  const urls = {
    android: APP_URLS.ANDROID_STORE,
    ios: APP_URLS.IOS_STORE,
    other: APP_URLS.OFFICIAL_SITE
  };
  
  return urls[deviceType];
};

/**
 * URLパスからディープリンク情報を抽出
 * @param pathname URLパス (例: /app/product/abc123)
 */
export const parseDeepLink = (pathname: string): DeepLinkInfo => {
  const segments = pathname.split('/').filter(Boolean);
  
  // /app/product/{id} または /app/profile/{id} の形式を解析
  if (segments.length >= 3 && segments[0] === 'app') {
    const type = segments[1];
    const id = segments[2];
    
    if (type === 'product') {
      return { type: 'product', id };
    } else if (type === 'profile') {
      return { type: 'profile', id };
    }
  }
  
  return { type: 'none', id: null };
};

/**
 * ディープリンク情報からアプリ内URLを生成
 * @param deepLinkInfo ディープリンク情報
 */
export const generateAppDeepLink = (deepLinkInfo: DeepLinkInfo): string | null => {
  if (deepLinkInfo.type === 'none' || !deepLinkInfo.id) {
    return null;
  }
  
  // カスタムURLスキームを使用
  if (deepLinkInfo.type === 'product') {
    return `${APP_URLS.CUSTOM_SCHEME}app/product/${deepLinkInfo.id}`;
  } else if (deepLinkInfo.type === 'profile') {
    return `${APP_URLS.CUSTOM_SCHEME}app/profile/${deepLinkInfo.id}`;
  }
  
  return null;
};

/**
 * Android用のintent URLを生成
 * @param deepLinkInfo ディープリンク情報
 */
export const generateAndroidIntentUrl = (deepLinkInfo: DeepLinkInfo): string => {
  const webUrl = window.location.href;
  const packageName = 'com.app.art_flea';
  
  let pathPart = '';
  if (deepLinkInfo.type !== 'none' && deepLinkInfo.id) {
    pathPart = `app/${deepLinkInfo.type}/${deepLinkInfo.id}`;
  }
  
  // Android Intent URLを生成
  // アプリがインストールされていない場合はGoogle Playにリダイレクト
  return `intent://${pathPart}#Intent;scheme=artflea;package=${packageName};S.browser_fallback_url=${encodeURIComponent(APP_URLS.ANDROID_STORE)};end`;
};
